import { DownloadOutlined, FilterOutlined, WalletOutlined } from "@ant-design/icons";
import { Table, Tooltip } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  cashConverter,
  dateForHumans,
  numberWithCommas,
} from "../../utils";
import { fetchCreditReseller } from "../../features/credit/creditSlice";
import CreditAddModal from "./CreditAddModal";
import CreditAddSelfModal from "./CreditAddSelfModal";
import FilterModal from "./FilterModal";
import svg38 from "../../assets/svg/svg38.svg";

function CreditTable() {
  const { loading, creditData, creditCount } = useSelector(
    (state) => state.credit
  );
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  const [prodd, setProdd] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenSelf, setIsModalOpenSelf] = useState(false);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [formData, setFormData] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const showModalSelf = () => {
    setIsModalOpenSelf(true);
  };

  const showModalFilter = () => {
    setIsModalOpenFilter(true);
  };

  const hasFilters = useMemo(
    () => Object.keys(formData || {}).length > 0,
    [formData]
  );

  const hasResellerName = creditData?.some(
    (item) =>
      item?.smsResellerName !== null && item?.smsResellerName !== undefined
  );

  const hasAccountName = creditData?.some(
    (item) =>
      item?.smsAccountName !== null && item?.smsAccountName !== undefined
  );

  const columns = [
    {
      title: "Created Date",
      render: (item) => <div>{dateForHumans(item)}</div>,
      dataIndex: "smsCreatedDate",
    },
    ...(hasResellerName
      ? [
          {
            title: "Reseller Name",
            render: (item) => <div>{item}</div>,
            dataIndex: "smsResellerName",
          },
        ]
      : []),
    ...(hasAccountName
      ? [
          {
            title: "Account Name",
            render: (item) => <div>{item}</div>,
            dataIndex: "smsAccountName",
          },
        ]
      : []),
    {
      title: "Previous Balance",
      render: (item) => <div>{cashConverter(item)}</div>,
      dataIndex: "smsPrevBal",
    },
    {
      title: "SMS Rate",
      dataIndex: "smsRate",
    },
    {
      title: "Amount",
      render: (item) => <div>{cashConverter(item)}</div>,
      dataIndex: "smsPayAmount",
    },
    {
      title: "Loaded Unit",
      render: (item) => <div>{numberWithCommas(item)}</div>,
      dataIndex: "smsLoaded",
    },
    {
      title: "New Balance",
      render: (item) => <div>{cashConverter(item)}</div>,
      dataIndex: "smsNewBal",
    },
    {
      title: "Created By",
      dataIndex: "smsCreatedByName",
    },
    {
      title: "Status",
      render: (item) => <div className="font-bold">{item}</div>,
      dataIndex: "crStatus",
    },
  ];

  const buildCreditRequest = (page = pageIndex, size = pageSize) => ({
    url:
      user?.layer === "TOP"
        ? "api/v2/credit/reseller"
        : user?.layer === "RESELLER"
        ? "api/v2/credit/reseller-account"
        : user?.layer === "ACCOUNT"
        ? "api/v2/credit/account"
        : null,
    limit: size,
    start: page,
    smsAccountName: formData?.smsAccountName,
    smsCreatedByName: formData?.smsCreatedByName,
    smsCreatedDate: formData?.smsCreatedDate,
    smsApprovedByName: formData?.smsApprovedByName,
    smsApprovedDate: formData?.smsApprovedDate,
    crStatus: formData?.crStatus,
  });

  const fetchCreditData = async (page = pageIndex, size = pageSize) => {
    await dispatch(fetchCreditReseller(buildCreditRequest(page, size)));
  };

  const handleClick = async () => {
    // Export logic intentionally unchanged.
  };

  const handleClearFilters = async () => {
    setFormData({});
    setPageIndex(0);

    await dispatch(
      fetchCreditReseller({
        url:
          user?.layer === "TOP"
            ? "api/v2/credit/reseller"
            : user?.layer === "RESELLER"
            ? "api/v2/credit/reseller-account"
            : user?.layer === "ACCOUNT"
            ? "api/v2/credit/account"
            : null,
        smsAccountName: null,
        smsCreatedByName: null,
        smsCreatedDate: null,
        smsApprovedByName: null,
        smsApprovedDate: null,
        crStatus: null,
      })
    );
  };

  useEffect(() => {
    fetchCreditData();
  }, []);

  return (
    <>
      <div className="w-full">
        <div className="weiser-surface-toolbar">
          <div className="weiser-surface-toolbar-left">
            <button
              type="button"
              className="weiser-surface-button is-primary"
              onClick={showModal}
            >
              <WalletOutlined />
              Load Account Credit
            </button>

            {user?.layer === "RESELLER" && (
              <button
                type="button"
                className="weiser-surface-button is-primary"
                onClick={showModalSelf}
              >
                <WalletOutlined />
                Load Self Credit
              </button>
            )}

            <button
              onClick={showModalFilter}
              type="button"
              className="weiser-surface-button"
            >
              <FilterOutlined />
              Filters
            </button>

            {hasFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="weiser-surface-link"
              >
                <img src={svg38} alt="clear filters" />
                Clear filters
              </button>
            )}
          </div>

          {creditData?.length > 0 && (
            <div className="weiser-surface-toolbar-right">
              <Tooltip placement="top" title={"Download Excel"}>
                <button
                  type="button"
                  onClick={handleClick}
                  className="weiser-surface-button is-ghost"
                >
                  <DownloadOutlined />
                  Export Excel
                </button>
              </Tooltip>
            </div>
          )}
        </div>

        <div className="weiser-table-shell mt-[1rem]">
          <Table
            className="w-full"
            scroll={{
              x: "max-content",
            }}
            rowKey={(record) => record?.reId}
            columns={columns}
            dataSource={creditData}
            loading={loading}
            pagination={{
              position: ["bottomCenter"],
              current: pageIndex + 1,
              total: creditCount,
              pageSize: pageSize,
              onChange: (page, size) => {
                setPageIndex(page - 1);
                setPageSize(size);
                fetchCreditData(page - 1, size);
              },
              showSizeChanger: false,
              hideOnSinglePage: true,
            }}
          />
        </div>

        <CreditAddModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          prodd={prodd}
        />

        <CreditAddSelfModal
          isModalOpen={isModalOpenSelf}
          setIsModalOpen={setIsModalOpenSelf}
          prodd={prodd}
        />

        <FilterModal
          isModalOpen={isModalOpenFilter}
          setIsModalOpen={setIsModalOpenFilter}
          formData={formData}
          setFormData={setFormData}
        />
      </div>
    </>
  );
}

export default CreditTable;
